import React, { useEffect, useRef } from 'react';
import { gsap, ScrollTrigger, prefersReducedMotion } from '../../utils/gsap';

/**
 * PersonalChapters — Kumrat + Uncertainty + Why I Build + Future
 *
 * Kumrat: enormous photo reveal via clip-path wipe. Slow, breathing.
 * Uncertainty: words drift apart → fragment → resolve cleanly.
 * Why I Build: quiet editorial statement.
 * Future: forward-looking horizon.
 */
const PersonalChapters = () => {
  const kumratWrapperRef = useRef(null);
  const kumratSceneRef = useRef(null);
  const kumratImgRef = useRef(null);
  const kumratRingsRef = useRef(null);
  const kumratTextRef = useRef(null);

  const uncertainWrapperRef = useRef(null);
  const uncertainSceneRef = useRef(null);
  const word1Ref = useRef(null);
  const word2Ref = useRef(null);
  const word3Ref = useRef(null);
  const resolveRef = useRef(null);

  const purposeRef = useRef(null);

  useEffect(() => {
    if (prefersReducedMotion()) return;

    let ctx;
    const setup = () => {
      ctx = gsap.context(() => {

        // === KUMRAT: 3-Phase Cinematic Scroll Sequence ===
        // Phase 1 (0–30%): Photo enters centered in viewport
        // Phase 2 (30–65%): Celestial orbital rings & stars rotate around the framed photo
        // Phase 3 (65–100%): Photo glides to the LEFT, quote text reveals on the RIGHT
        const kumratTl = gsap.timeline({
          scrollTrigger: {
            trigger: kumratWrapperRef.current,
            start: 'top top',
            end: '+=240%',
            pin: kumratSceneRef.current,
            scrub: 1,
            anticipatePin: 1,
          }
        });

        const isDesktop = window.innerWidth > 768;

        kumratTl
          // Phase 1: Initial DEAD CENTER appearance (photo & orbital rings centered in viewport)
          .fromTo(kumratImgRef.current,
            { opacity: 0.85, scale: 0.9, x: 0, y: 25 },
            { opacity: 1, scale: 1, x: 0, y: 0, ease: 'power2.out', duration: 1 },
            0
          )
          .fromTo(kumratRingsRef.current,
            { opacity: 0, scale: 0.8 },
            { opacity: 1, scale: 1, ease: 'power2.out', duration: 1 },
            0.2
          )
          
          // Phase 2: Orbital Rings Rotate around the centered photo
          .to(kumratRingsRef.current, {
            rotation: 180,
            scale: 1.08,
            ease: 'none',
            duration: 1.5
          }, 1)
          .to(kumratImgRef.current, {
            scale: 1.03,
            ease: 'sine.inOut',
            duration: 1.5
          }, 1)

          // Phase 3: Frame glides from DEAD CENTER to LEFT, text reveals on RIGHT
          .to(kumratImgRef.current, {
            x: isDesktop ? '-22vw' : 0,
            y: isDesktop ? 0 : '-14vh',
            scale: 1,
            ease: 'power3.inOut',
            duration: 1.5
          }, 2.5)
          .to(kumratRingsRef.current, {
            rotation: 360,
            opacity: 0.6,
            ease: 'power3.inOut',
            duration: 1.5
          }, 2.5)
          .fromTo(kumratTextRef.current,
            { opacity: 0, x: isDesktop ? '30px' : 0, y: isDesktop ? 0 : '30px' },
            { opacity: 1, x: 0, y: 0, ease: 'power3.out', duration: 1.2 },
            2.8
          )
          // Phase 4: Smooth exit dissolution into Chapter 10
          .to([kumratImgRef.current, kumratRingsRef.current, kumratTextRef.current], {
            opacity: 0,
            y: '-40px',
            ease: 'power2.in',
            duration: 1
          }, 4.2);

        // === UNCERTAINTY: Words drift → fragment → resolve ===
        const uncertainTl = gsap.timeline({
          scrollTrigger: {
            trigger: uncertainWrapperRef.current,
            start: 'top top',
            end: '+=200%',
            pin: uncertainSceneRef.current,
            scrub: 1,
            anticipatePin: 1,
          }
        });

        // Initial — words appear stable
        uncertainTl
          .fromTo([word1Ref.current, word2Ref.current, word3Ref.current],
            { opacity: 0 },
            { opacity: 1, stagger: 0.2, duration: 0.5 },
            0
          )
          // Words start drifting
          .to(word1Ref.current, {
            x: '-3vw', y: '-1.5vh', letterSpacing: '0.12em', opacity: 0.7,
            ease: 'sine.inOut', duration: 1
          }, 0.8)
          .to(word2Ref.current, {
            x: '2.5vw', y: '1vh', letterSpacing: '0.2em', opacity: 0.6,
            ease: 'sine.inOut', duration: 1
          }, 0.9)
          .to(word3Ref.current, {
            x: '-1.5vw', y: '2.5vh', letterSpacing: '0.08em', opacity: 0.5,
            ease: 'sine.inOut', duration: 1
          }, 1.0)
          // Fragment — more extreme drift
          .to(word1Ref.current, {
            x: '-8vw', y: '-4vh', opacity: 0.25, rotate: -2,
            ease: 'power1.inOut', duration: 0.8
          }, 1.9)
          .to(word2Ref.current, {
            x: '6vw', y: '3.5vh', opacity: 0.2, rotate: 1.5,
            ease: 'power1.inOut', duration: 0.8
          }, 2.0)
          .to(word3Ref.current, {
            x: '-4vw', y: '6vh', opacity: 0.15, rotate: -1,
            ease: 'power1.inOut', duration: 0.8
          }, 2.1)
          // Resolve — words fade out, resolution appears
          .to([word1Ref.current, word2Ref.current, word3Ref.current],
            { opacity: 0, duration: 0.4 },
            2.8
          )
          .fromTo(resolveRef.current,
            { opacity: 0, y: 10 },
            { opacity: 1, y: 0, ease: 'power3.out', duration: 0.7 },
            2.9
          );
      });
    };

    ScrollTrigger.refresh();
    setup();
    return () => { if (ctx) ctx.revert(); };
  }, []);

  return (
    <div id="section-personal" className="personal-chapters-outer">

      {/* === KUMRAT === */}
      <section ref={kumratWrapperRef} className="kumrat-scroll-wrapper">
        <div ref={kumratSceneRef} className="kumrat-scene">

          {/* SVG Mountains & Stars Background */}
          <div className="kumrat-svg-bg" aria-hidden="true">
            <svg viewBox="0 0 1200 600" width="100%" height="100%" preserveAspectRatio="xMidYMid slice">
              <defs>
                <linearGradient id="sky" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#080c14" />
                  <stop offset="45%" stopColor="#0d182b" />
                  <stop offset="100%" stopColor="#091410" />
                </linearGradient>
                <linearGradient id="mtn1" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#14281a" />
                  <stop offset="100%" stopColor="#080e0a" />
                </linearGradient>
                <linearGradient id="mtn2" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#1e3623" />
                  <stop offset="100%" stopColor="#080e0a" />
                </linearGradient>
                <linearGradient id="river" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="rgba(56,189,248,0.2)" />
                  <stop offset="100%" stopColor="rgba(56,189,248,0.05)" />
                </linearGradient>
              </defs>
              <rect width="1200" height="600" fill="url(#sky)" />
              {/* Stars */}
              {Array.from({ length: 70 }, (_, i) => (
                <circle key={i}
                  cx={Math.sin(i * 137.5 * Math.PI / 180) * 600 + 600}
                  cy={Math.cos(i * 83.7 * Math.PI / 180) * 140 + 90}
                  r={Math.random() * 1.3 + 0.3}
                  fill="rgba(255,255,255,0.75)"
                  opacity={0.3 + Math.random() * 0.7}
                />
              ))}
              {/* Far & near mountains */}
              <path d="M0 380 L120 280 L220 330 L340 260 L450 310 L560 240 L680 290 L790 230 L900 270 L1020 230 L1120 280 L1200 260 L1200 600 L0 600 Z" fill="url(#mtn1)" opacity="0.8" />
              <path d="M0 460 L90 380 L190 420 L310 360 L430 400 L530 340 L650 380 L780 320 L910 370 L1020 340 L1120 390 L1200 350 L1200 600 L0 600 Z" fill="url(#mtn2)" />
              {/* Pine trees */}
              {[40, 100, 160, 220, 920, 980, 1040, 1100].map((x, i) => (
                <g key={i}>
                  <polygon points={`${x},${460 - i * 3} ${x - 16},510 ${x + 16},510`} fill="#07140b" opacity="0.9" />
                  <polygon points={`${x},${438 - i * 3} ${x - 11},475 ${x + 11},475`} fill="#091a0e" opacity="0.85" />
                </g>
              ))}
              {/* River */}
              <path d="M350 510 Q450 500 550 515 Q650 530 750 510 Q850 490 950 500 L950 550 Q850 535 750 550 Q650 565 550 550 Q450 535 350 550 Z" fill="url(#river)" />
              {/* Moon */}
              <circle cx="920" cy="75" r="30" fill="rgba(255,248,220,0.12)" />
            </svg>
          </div>

          {/* Chapter annotation */}
          <div className="kumrat-annotation font-mono">
            CH. 09 &nbsp;// KUMRAT VALLEY
          </div>

          {/* Standalone Centered Photo Stage & Orbital Rings Container */}
          <div ref={kumratImgRef} className="kumrat-photo-stage">
            {/* Celestial Orbital Rings around the frame */}
            <div ref={kumratRingsRef} className="kumrat-orbital-rings" aria-hidden="true">
              <svg viewBox="0 0 600 600" className="kumrat-orbital-svg">
                <circle cx="300" cy="300" r="255" fill="none" stroke="rgba(212,163,89,0.35)" strokeWidth="1.5" strokeDasharray="8 8" />
                <circle cx="300" cy="300" r="285" fill="none" stroke="rgba(212,163,89,0.2)" strokeWidth="1" strokeDasharray="14 10" />
                <circle cx="300" cy="300" r="298" fill="none" stroke="rgba(212,163,89,0.12)" strokeWidth="0.75" />
                {/* Compass ticks */}
                <line x1="300" y1="5" x2="300" y2="25" stroke="#d4a359" strokeWidth="2.5" />
                <line x1="300" y1="575" x2="300" y2="595" stroke="#d4a359" strokeWidth="2.5" />
                <line x1="5" y1="300" x2="25" y2="300" stroke="#d4a359" strokeWidth="2.5" />
                <line x1="575" y1="300" x2="595" y2="300" stroke="#d4a359" strokeWidth="2.5" />
                {/* Celestial accent stars */}
                <circle cx="140" cy="120" r="3.5" fill="#d4a359" />
                <circle cx="460" cy="480" r="3.5" fill="#d4a359" />
                <circle cx="480" cy="140" r="2.5" fill="#ffffff" />
                <circle cx="120" cy="460" r="2.5" fill="#ffffff" />
              </svg>
            </div>

            {/* Golden Framed Portrait Photo */}
            <div className="kumrat-gold-frame-portrait">
              {/* Corner crosshairs */}
              <div className="frame-corner top-left font-mono">+</div>
              <div className="frame-corner top-right font-mono">+</div>
              <div className="frame-corner bottom-left font-mono">+</div>
              <div className="frame-corner bottom-right font-mono">+</div>

              {/* Frame Header Meta */}
              <div className="frame-meta-bar frame-meta-top font-mono">
                <span>[ EXPOSURE: KUMRAT VALLEY ]</span>
                <span>[ LAT 35.5342° N ]</span>
              </div>

              {/* Real Portrait Photo */}
              <div className="kumrat-portrait-photo-wrapper">
                <img
                  src="/assets/astro.png"
                  alt="Kumrat Valley Astrophotography and Night Sky observation by Shawaiz Ali Rehman"
                  className="kumrat-portrait-photo"
                  loading="lazy"
                  decoding="async"
                />
              </div>

              {/* Frame Footer Meta */}
              <div className="frame-meta-bar frame-meta-bottom font-mono">
                <span>ASTROPHOTOGRAPHY &amp; OBSERVATION</span>
                <span>SHAWAIZ ALI REHMAN</span>
              </div>
            </div>
          </div>

          {/* Right-side Text Panel */}
          <div ref={kumratTextRef} className="kumrat-text-panel">
            <div className="kumrat-text-caption font-mono">CH. 09 // KUMRAT VALLEY PERSPECTIVE</div>
            <blockquote className="kumrat-text-quote font-display">
              "Stepping back under the Kumrat night sky gave me clarity.<br />
              What we build matters most when it stays grounded<br />
              in human experience and real life."
            </blockquote>
            <div className="kumrat-meta-details font-mono">
              <span>ELEVATION: 2,400M</span> &nbsp;·&nbsp; <span>ASTROPHOTOGRAPHY</span> &nbsp;·&nbsp; <span>2024</span>
            </div>
          </div>
        </div>
      </section>

      {/* === UNCERTAINTY === */}
      <section ref={uncertainWrapperRef} className="uncertain-scroll-wrapper">
        <div ref={uncertainSceneRef} className="uncertain-scene">
          <div className="uncertain-annotation font-mono">
            CH. 10 &nbsp;// HONEST UNCERTAINTY
          </div>

          {/* Drifting words */}
          <div className="uncertain-words">
            <div ref={word1Ref} className="uncertain-word uncertain-word-1 font-display">
              I DON'T KNOW
            </div>
            <div ref={word2Ref} className="uncertain-word uncertain-word-2 font-display">
              IF I'M CHOOSING
            </div>
            <div ref={word3Ref} className="uncertain-word uncertain-word-3 font-display">
              THE RIGHT PATH.
            </div>
          </div>

          {/* Resolution */}
          <div ref={resolveRef} className="uncertain-resolve">
            <div className="uncertain-resolve-line1 font-display">BUT I KNOW</div>
            <div className="uncertain-resolve-line2 font-display">
              I WANT TO <span className="uncertain-resolve-accent">BUILD.</span>
            </div>
            <div className="uncertain-resolve-sub font-mono">AND THAT'S ENOUGH TO START.</div>
          </div>
        </div>
      </section>

      {/* === CHAPTER 11 — THE HORIZON & PURPOSE === */}
      <section ref={purposeRef} className="ch11-horizon-section">
        {/* Top-left chapter annotation */}
        <div className="ch11-annotation font-mono">
          CH. 11 &nbsp;// THE HORIZON
        </div>

        <div className="ch11-container">
          <div className="ch11-header">
            <h2 className="ch11-headline font-display">
              Why I Build &amp;<br />
              <span className="ch11-accent">What Lies Ahead.</span>
            </h2>
          </div>

          <blockquote className="ch11-quote font-display">
            "I don't build software just to accumulate technical badges.<br />
            I build to make people's lives tangibly better."
          </blockquote>

          <div className="ch11-pillars">
            {[
              { num: '01', name: 'Human Impact', desc: 'Building software that solves tangible problems for real users.' },
              { num: '02', name: 'Technical Craft', desc: 'Engineering low-latency, resilient, and well-architected systems.' },
              { num: '03', name: 'Relentless Growth', desc: 'Pushing across mobile, web, AI, and systems engineering.' }
            ].map((p, i) => (
              <div key={i} className="ch11-pillar-card">
                <span className="ch11-pillar-num font-mono">{p.num}</span>
                <h4 className="ch11-pillar-name font-heading">{p.name}</h4>
                <p className="ch11-pillar-desc font-body">{p.desc}</p>
              </div>
            ))}
          </div>

          <p className="ch11-future-body font-body">
            Whether through high-performance mobile apps, native Android systems, AI healthcare pipelines,
            or low-level systems engineering — my commitment remains constant: relentless growth,
            technical depth, and software built with purpose.
          </p>
        </div>
      </section>

      <style>{`
        .personal-chapters-outer {
          background: #080a0f;
        }

        /* === KUMRAT === */
        .kumrat-scroll-wrapper {
          position: relative;
          height: 320vh;
          background: #080a0f;
        }

        .kumrat-scene {
          position: relative;
          width: 100%;
          height: 100vh;
          overflow: hidden;
          background: #080a0f;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .kumrat-scene-bg {
          position: absolute;
          inset: 0;
          background: #080a0f;
          z-index: 0;
        }

        .kumrat-svg-bg {
          position: absolute;
          inset: 0;
          z-index: 1;
          pointer-events: none;
          opacity: 0.85;
        }

        .kumrat-annotation {
          position: absolute;
          top: 8vh;
          left: 5vw;
          font-size: 0.68rem;
          letter-spacing: 0.2em;
          color: rgba(212, 163, 89, 0.7);
          z-index: 10;
          font-weight: 600;
        }

        .kumrat-photo-stage {
          position: absolute;
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%);
          z-index: 5;
          display: flex;
          align-items: center;
          justify-content: center;
          will-change: transform, opacity;
        }

        .kumrat-orbital-rings {
          position: absolute;
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%);
          width: min(650px, 92vw);
          height: min(650px, 92vw);
          pointer-events: none;
          z-index: 1;
          will-change: transform, opacity;
        }

        .kumrat-orbital-svg {
          width: 100%;
          height: 100%;
        }

        .kumrat-gold-frame-portrait {
          position: relative;
          z-index: 2;
          width: min(370px, 80vw);
          height: clamp(480px, 64vh, 580px);
          border: 2px solid #d4a359;
          border-radius: 8px;
          background: rgba(10, 14, 22, 0.94);
          box-shadow: 0 0 35px rgba(212, 163, 89, 0.3), 0 20px 50px rgba(0, 0, 0, 0.85);
          padding: 0.75rem 0.9rem;
          display: flex;
          flex-direction: column;
          gap: 0.5rem;
        }

        .frame-corner {
          position: absolute;
          font-size: 1.1rem;
          color: #d4a359;
          line-height: 1;
          pointer-events: none;
          font-weight: 700;
        }

        .frame-corner.top-left { top: -7px; left: -7px; }
        .frame-corner.top-right { top: -7px; right: -7px; }
        .frame-corner.bottom-left { bottom: -7px; left: -7px; }
        .frame-corner.bottom-right { bottom: -7px; right: -7px; }

        .frame-meta-bar {
          display: flex;
          justify-content: space-between;
          align-items: center;
          font-size: 0.62rem;
          letter-spacing: 0.14em;
          color: #d4a359;
          opacity: 0.9;
        }

        .kumrat-portrait-photo-wrapper {
          position: relative;
          width: 100%;
          flex: 1;
          overflow: hidden;
          border-radius: 4px;
          border: 1px solid rgba(212, 163, 89, 0.35);
        }

        .kumrat-portrait-photo {
          width: 100%;
          height: 100%;
          object-fit: cover;
          object-position: center center;
          display: block;
          filter: contrast(1.05) brightness(0.95);
          transition: transform 0.8s ease;
        }

        .kumrat-portrait-photo:hover {
          transform: scale(1.03);
        }

        .kumrat-text-panel {
          position: absolute;
          top: 50%;
          right: clamp(4vw, 8vw, 12vw);
          transform: translateY(-50%);
          z-index: 6;
          display: flex;
          flex-direction: column;
          align-items: flex-start;
          text-align: left;
          max-width: 480px;
          will-change: opacity, transform;
        }

        .kumrat-text-caption {
          font-size: 0.68rem;
          letter-spacing: 0.22em;
          color: var(--accent-gold);
          margin-bottom: 1rem;
          font-weight: 600;
        }

        .kumrat-text-quote {
          font-size: clamp(1.15rem, 2.3vw, 1.85rem);
          line-height: 1.45;
          color: #f3f4f6;
          font-style: italic;
          font-family: 'Playfair Display', Georgia, serif;
          font-weight: 400;
          border-left: 2px solid var(--accent-gold);
          padding-left: 1.25rem;
          margin: 0 0 1.25rem 0;
        }

        .kumrat-meta-details {
          font-size: 0.65rem;
          letter-spacing: 0.16em;
          color: rgba(212, 163, 89, 0.75);
        }



        .kumrat-text-caption {
          font-size: 0.65rem;
          letter-spacing: 0.22em;
          color: var(--accent-gold);
          margin-bottom: 0.75rem;
          font-weight: 600;
        }

        .kumrat-text-quote {
          font-size: clamp(1.1rem, 2.3vw, 1.7rem);
          line-height: 1.5;
          color: #f3f4f6;
          font-style: italic;
          font-family: 'Playfair Display', Georgia, serif;
          font-weight: 400;
          border: none;
          padding: 0;
          max-width: 720px;
          margin: 0 auto;
        }

        /* === UNCERTAINTY === */
        .uncertain-scroll-wrapper {
          position: relative;
          height: 280vh;
          background: #080a0f;
        }

        .uncertain-scene {
          position: relative;
          width: 100%;
          height: 100vh;
          overflow: hidden;
          background: #080a0f;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .uncertain-annotation {
          position: absolute;
          top: 8vh;
          left: 5vw;
          font-size: 0.68rem;
          letter-spacing: 0.2em;
          color: rgba(212, 163, 89, 0.5);
        }

        .uncertain-words {
          position: absolute;
          inset: 0;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          gap: 0.75rem;
          text-align: center;
        }

        .uncertain-word {
          font-size: clamp(1.8rem, 4.5vw, 4rem);
          font-weight: 800;
          letter-spacing: -0.02em;
          color: rgba(243, 244, 246, 0.6);
          will-change: transform, opacity, letter-spacing;
          line-height: 1.1;
        }

        .uncertain-word-1 { color: rgba(243, 244, 246, 0.7); }
        .uncertain-word-2 { color: rgba(243, 244, 246, 0.5); }
        .uncertain-word-3 { color: rgba(243, 244, 246, 0.4); }

        .uncertain-resolve {
          position: absolute;
          inset: 0;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          text-align: center;
          gap: 0.5rem;
          will-change: opacity, transform;
        }

        .uncertain-resolve-line1 {
          font-size: clamp(1.5rem, 4vw, 3.5rem);
          font-weight: 700;
          color: rgba(243, 244, 246, 0.7);
          letter-spacing: -0.02em;
        }

        .uncertain-resolve-line2 {
          font-size: clamp(2rem, 6vw, 5rem);
          font-weight: 800;
          letter-spacing: -0.03em;
          color: #f3f4f6;
        }

        .uncertain-resolve-accent {
          color: var(--accent-gold);
          font-style: italic;
          font-family: 'Playfair Display', Georgia, serif;
        }

        .uncertain-resolve-sub {
          font-size: 0.65rem;
          letter-spacing: 0.22em;
          color: rgba(107, 114, 128, 0.6);
          margin-top: 1.5rem;
        }

        /* === CHAPTER 11 — HORIZON & PURPOSE === */
        .ch11-horizon-section {
          min-height: 100vh;
          padding: 6rem 1.5rem;
          background: #080a0f;
          display: flex;
          align-items: center;
          justify-content: center;
          border-top: 1px solid rgba(212, 163, 89, 0.15);
          position: relative;
        }

        .ch11-annotation {
          position: absolute;
          top: 8vh;
          left: 5vw;
          font-size: 0.68rem;
          letter-spacing: 0.2em;
          color: rgba(212, 163, 89, 0.7);
          z-index: 10;
          font-weight: 600;
        }

        .ch11-container {
          max-width: 900px;
          width: 100%;
          margin: 0 auto;
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 2.5rem;
          text-align: center;
        }

        .ch11-header {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 1rem;
        }

        .ch11-tag {
          color: #d4a359;
          font-size: 0.75rem;
          letter-spacing: 0.22em;
          display: flex;
          align-items: center;
          gap: 0.6rem;
          background: rgba(212, 163, 89, 0.08);
          border: 1px solid rgba(212, 163, 89, 0.25);
          padding: 0.4rem 1rem;
          border-radius: 100px;
        }

        .ch11-dot {
          width: 6px;
          height: 6px;
          border-radius: 50%;
          background: #d4a359;
          box-shadow: 0 0 10px #d4a359;
        }

        .ch11-headline {
          font-size: clamp(2.4rem, 5.5vw, 4rem);
          line-height: 1.15;
          color: #ffffff;
          margin: 0;
          font-weight: 800;
          letter-spacing: -0.03em;
        }

        .ch11-accent {
          color: #d4a359;
          font-style: italic;
          font-family: 'Playfair Display', Georgia, serif;
          font-weight: 500;
        }

        .ch11-quote {
          font-size: clamp(1.2rem, 2.4vw, 1.8rem);
          line-height: 1.5;
          color: #f3f4f6;
          font-style: italic;
          font-family: 'Playfair Display', Georgia, serif;
          margin: 0;
          padding: 0 1rem;
          max-width: 780px;
        }

        .ch11-pillars {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
          gap: 1.25rem;
          width: 100%;
        }

        .ch11-pillar-card {
          background: rgba(16, 21, 31, 0.75);
          border: 1px solid rgba(255, 255, 255, 0.08);
          border-radius: 12px;
          padding: 1.5rem;
          text-align: left;
          display: flex;
          flex-direction: column;
          gap: 0.5rem;
        }

        .ch11-pillar-num {
          font-size: 0.72rem;
          letter-spacing: 0.16em;
          color: #d4a359;
          font-weight: 700;
        }

        .ch11-pillar-name {
          font-size: 1.1rem;
          color: #ffffff;
          margin: 0;
          font-weight: 700;
        }

        .ch11-pillar-desc {
          font-size: 0.85rem;
          color: rgba(156, 163, 175, 0.85);
          line-height: 1.55;
          margin: 0;
        }

        .ch11-future-body {
          font-size: clamp(0.9rem, 1.2vw, 1.05rem);
          line-height: 1.7;
          color: rgba(209, 213, 219, 0.85);
          max-width: 720px;
          margin: 0 auto;
        }

        @media (max-width: 768px) {
          .kumrat-scroll-wrapper { height: 280vh; }
          .uncertain-scroll-wrapper { height: 260vh; }

          /* All chapter annotations safely below topbar */
          .kumrat-annotation,
          .uncertain-annotation,
          .ch11-annotation {
            top: calc(56px + 12px);
            left: 5vw;
            font-size: 0.62rem;
            letter-spacing: 0.16em;
          }

          .kumrat-scene {
            display: flex !important;
            flex-direction: column !important;
            align-items: center !important;
            justify-content: center !important;
            gap: 0.85rem !important;
            padding: 4.5rem 1rem 2rem 1rem !important;
          }

          .kumrat-photo-stage {
            position: relative !important;
            top: auto !important;
            left: auto !important;
            right: auto !important;
            bottom: auto !important;
            transform: none !important;
            margin: 0 auto;
            width: min(290px, 84vw) !important;
          }

          .kumrat-gold-frame-portrait {
            width: 100% !important;
            height: clamp(240px, 38vh, 310px) !important;
          }

          /* Header meta inside gold frame — fit cleanly without truncation */
          .frame-meta-bar {
            font-size: 0.52rem !important;
            letter-spacing: 0.05em !important;
            gap: 0.25rem;
            padding: 0.2rem 0.4rem;
          }

          .frame-meta-bar span {
            white-space: nowrap;
            overflow: hidden;
            text-overflow: ellipsis;
          }

          /* Quote panel sitting cleanly BELOW the photo frame without overlap */
          .kumrat-text-panel {
            position: relative !important;
            top: auto !important;
            bottom: auto !important;
            right: auto !important;
            left: auto !important;
            transform: none !important;
            margin-top: 0.5rem !important;
            width: 90vw !important;
            max-width: 440px !important;
            align-items: center !important;
            text-align: center !important;
          }

          .kumrat-text-caption {
            font-size: 0.58rem;
            letter-spacing: 0.14em;
            text-align: center;
            margin-bottom: 0.4rem;
          }

          .kumrat-text-quote {
            border-left: none !important;
            padding-left: 0 !important;
            font-size: clamp(0.95rem, 3.8vw, 1.25rem) !important;
            line-height: 1.45 !important;
            text-align: center !important;
            margin: 0 auto 0.5rem auto !important;
          }

          .kumrat-meta-details {
            font-size: 0.58rem;
            letter-spacing: 0.1em;
          }

          /* Uncertainty words */
          .uncertain-word {
            font-size: clamp(1.6rem, 8vw, 3rem);
            padding: 0 0.5rem;
          }

          .uncertain-resolve-line1 {
            font-size: clamp(1.3rem, 5.5vw, 2.2rem);
          }

          .uncertain-resolve-line2 {
            font-size: clamp(1.7rem, 8vw, 3rem);
          }

          .uncertain-resolve-sub {
            font-size: 0.58rem;
            letter-spacing: 0.16em;
          }

          /* Chapter 11 Horizon on mobile */
          .ch11-horizon-section {
            padding: 5rem 1rem 4rem 1rem;
          }

          .ch11-headline {
            font-size: clamp(1.8rem, 8vw, 2.8rem);
          }

          .ch11-quote {
            font-size: clamp(1.05rem, 4.5vw, 1.4rem);
            padding: 0 0.5rem;
          }

          .ch11-pillars {
            grid-template-columns: 1fr;
            gap: 1rem;
          }

          .ch11-pillar-card {
            padding: 1.25rem;
          }

          .ch11-future-body {
            font-size: 0.85rem;
            line-height: 1.6;
            padding: 0 0.5rem;
          }
        }
      `}</style>
    </div>
  );
};

export default PersonalChapters;
